import { useEffect, useState } from 'react';
import { ChevronDown, MapPin, SlidersHorizontal, Store, User, Users, UsersRound } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useFilters } from '@/lib/filterContext';
import { fetchOptions } from '@/services/optionsService';
import { MultiSelect } from '@/components/MultiSelect';
import { DateFilter } from '@/components/DateFilter';
import { CompanySelect } from '@/components/CompanySelect';
import { ViewToggle } from '@/components/ViewToggle';
import { getCompanies, isSuperAdmin } from '@/services/authService';

const Divider = () => <span className="mx-1 h-7 w-px self-center bg-slate-200" />;

export function FilterBar({ onOpenRace }: { onOpenRace?: () => void }) {
  const { companies, scope, view, period, setScope } = useFilters();
  const [open, setOpen] = useState(false);

  // En móvil los filtros de scope se colapsan en un panel ("Filtros"); desktop = fila.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // SmartBamboo no tiene Región/Grupo y su Equipo de venta ES la Sucursal (crm_team).
  // Con SOLO SB activo se ocultan esos filtros (no aplican; hoy solo vaciarían el tablero).
  const sbOnly = companies.length > 0 && companies.every((c) => c === 'sb');

  // Al quedar en SB-only se limpian esas dimensiones del scope: si quedaran seleccionadas
  // (ocultas) seguirían excluyendo SB en el BFF y el tablero saldría vacío sin explicación.
  useEffect(() => {
    if (sbOnly && (scope.regions?.length || scope.teams?.length || scope.groups?.length)) {
      setScope({ ...scope, regions: [], teams: [], groups: [] });
    }
  }, [sbOnly, scope, setScope]);

  // La Sucursal seleccionada cascadea las opciones de Equipo (re-consulta al cambiar).
  // Empleado/Grupo cascadean por Sucursal/Equipo/Región + período → re-consulta al cambiarlos.
  const { data: options } = useQuery({
    queryKey: ['options', companies, scope.branches, scope.teams, scope.groups, scope.regions, period],
    queryFn: () => fetchOptions(companies, scope, period),
  });

  // # de filtros de scope activos (para el badge del botón "Filtros" en móvil).
  const activeScope = [scope.regions, scope.branches, scope.teams, scope.groups, scope.employees].filter(
    (a) => a?.length,
  ).length;

  // Ocultar filtros que NO aportan: si el usuario solo tiene 1 compañía (ej. gerente de
  // 1 plaza) el selector de Compañía sobra; Región/Sucursal se ocultan cuando el BFF
  // devuelve ≤1 opción (nada que elegir). Equipo siempre se mantiene cuando aplica.
  const showCompany = isSuperAdmin() || getCompanies().length > 1;
  const showRegion = (options?.regions?.length ?? 0) > 1;
  const showBranches = (options?.branches?.length ?? 0) > 1;

  // Filtros de scope (mismos controles en desktop y en el panel móvil; cambia el ancho).
  const scopeFilters = (fullWidth: boolean) => (
    <>
      {/* Región: dimensión de Odoo (VG/Piele). Oculta en SB-only y si hay ≤1 región. */}
      {!sbOnly && showRegion && (
        <MultiSelect
          fullWidth={fullWidth}
          label="Región"
          icon={MapPin}
          allLabel="Todas las regiones"
          options={options?.regions ?? []}
          selected={scope.regions ?? []}
          onChange={(regions) => setScope({ ...scope, regions })}
        />
      )}
      {/* Sucursal: oculta si el usuario solo puede ver 1 (ej. gerente de 1 plaza). En SB-only
          la "sucursal" ES el equipo de ventas (crm_team) → el label lo refleja. */}
      {showBranches && (
        <MultiSelect
          fullWidth={fullWidth}
          label={sbOnly ? 'Equipo de ventas' : 'Sucursal'}
          icon={Store}
          allLabel={sbOnly ? 'Todos los equipos' : 'Todas las sucursales'}
          options={options?.branches ?? []}
          selected={scope.branches ?? []}
          onChange={(branches) => setScope({ ...scope, branches })}
        />
      )}
      {/* Equipo de venta: distinto del PDV en VG/Piele (ej. Telemarketing). En SB el equipo
          ES la Sucursal (crm_team) → redundante, se oculta en SB-only. */}
      {!sbOnly && (
        <MultiSelect
          fullWidth={fullWidth}
          label="Equipo"
          icon={Users}
          allLabel="Todos los equipos"
          options={options?.teams ?? []}
          selected={scope.teams ?? []}
          onChange={(teams) => setScope({ ...scope, teams })}
        />
      )}
      {/* Grupo/Empleado SOLO en vista "Por Vendedor" (acotan la atribución por comisión).
          Grupo es dimensión de Odoo → oculto en SB-only; Empleado sí aplica a SB (comisionista). */}
      {view === 'vendedor' && !sbOnly && (
        <MultiSelect
          fullWidth={fullWidth}
          label="Grupo"
          icon={UsersRound}
          options={options?.groups ?? []}
          selected={scope.groups ?? []}
          onChange={(groups) => setScope({ ...scope, groups })}
        />
      )}
      {view === 'vendedor' && (
        <MultiSelect
          fullWidth={fullWidth}
          label="Empleado"
          icon={User}
          searchable
          options={options?.employees ?? []}
          selected={scope.employees ?? []}
          onChange={(employees) => setScope({ ...scope, employees })}
        />
      )}
    </>
  );

  // 🏁 Carrera del día: vive en la barra de filtros, alineado a la derecha. Compacto en
  // móvil (solo el banderín) para no desbordar la fila.
  const raceButton = (compact: boolean, extra = '') =>
    onOpenRace ? (
      <button
        onClick={onOpenRace}
        title="Carrera del día"
        aria-label="Carrera del día"
        className={`flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 ${extra}`}
      >
        <span>🏁</span>
        {!compact && <span>Carrera del día</span>}
      </button>
    ) : null;

  const vendedorBadge =
    view === 'vendedor' || scope.groups?.length || scope.employees?.length ? (
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
        {scope.groups?.length || scope.employees?.length
          ? 'Por vendedor · acotado a la selección'
          : 'Por vendedor · comisión acreditada'}
      </span>
    ) : null;

  // ── Móvil: vista + período arriba, resto bajo "Filtros" ──────────────────────
  if (isMobile) {
    return (
      <div className="border-b border-slate-200 bg-white px-4 py-2.5">
        <div className="space-y-2">
          <ViewToggle />
          <div className="flex items-center gap-2">
            <DateFilter />
            {raceButton(true, 'ml-auto')}
            <button
              onClick={() => setOpen((o) => !o)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-slate-400"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtros
              {activeScope > 0 && (
                <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white">
                  {activeScope}
                </span>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        {open && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {showCompany && (
              <div className="col-span-2">
                <CompanySelect fullWidth />
              </div>
            )}
            {scopeFilters(true)}
            {vendedorBadge && <div className="col-span-2">{vendedorBadge}</div>}
          </div>
        )}
      </div>
    );
  }

  // ── Desktop: una sola fila con divisores (sin cambios) ───────────────────────
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-6 py-3">
      <ViewToggle />
      <Divider />
      <DateFilter />
      {showCompany && (
        <>
          <Divider />
          <CompanySelect />
        </>
      )}
      <Divider />
      {scopeFilters(false)}
      {vendedorBadge}
      {raceButton(false, 'ml-auto')}
    </div>
  );
}
